import * as React from "react";

import {
  CSFile,
  TOpenOption,
  UpdateControl,
  cClientFileType,
  cJSonFunctionCloseView,
  CSUpdateCommandItem,
  UpdateOpenDialogContent,
} from "../common/communication.base";
import { ClientNclButton, NclSilentOpenDialog, NclSilentOpenDialogContent } from "../common/components.ncl";
import { VisualContext } from "../common/visualContext";
import { K2Button } from "./K2Button";
import { AcquireControl, K2ComponentState, StyleHelper, withContext, WithContextPlacementProps } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import { K2TruncateText } from "./K2TruncateText";

const fileDialogView = (props: K2RuleWithVCXProps) => ({
  height: "100%",
  width: "100%",
  backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.ContentColorBck1),
});

const dialogView = (props: K2RuleWithVCXProps) => ({
  height: "100%",
  width: "100%",
  flexDirection: "column" as const,
});

const dialogImg = (props: K2RuleWithVCXProps) => ({
  margin: "auto",
  flex: 3,
});

const dialogFiles = (props: K2RuleWithVCXProps) => ({
  overflow: "auto",
  flex: 4,
  flexDirection: "column" as const,
});

const dialogMessage = (props: K2RuleWithVCXProps) => ({
  fontSize: "16pt",
  fontWeight: "bold" as const,
  flex: 2,
  alignSelf: "center" as const,
  alignItems: "center" as const,
});

const dialogBtns = (props: K2RuleWithVCXProps) => ({
  flexDirection: "row" as const,
  flex: "0 0 auto",
  height: `${props.vcx.InputControl.getInputHeight(2, false, false)}px`,
});

interface OpenFileDialogState extends K2ComponentState<UpdateOpenDialogContent> {
  filesInMemoryCount?: number;
  title: string;
  readyToUpload?: boolean;
}

class _OpenDialogContent extends React.PureComponent<WithContextPlacementProps, OpenFileDialogState> {
  private control: NclSilentOpenDialogContent;
  private allowMultipleSelect: boolean;
  private input: HTMLInputElement;
  private timeH: NodeJS.Timer;
  private files: Array<CSFile>;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclSilentOpenDialogContent;
    }) as NclSilentOpenDialogContent;
    this.files = [];
    if (this.control.Parent instanceof NclSilentOpenDialog) {
      this.allowMultipleSelect = (this.control.Parent.Ncl.FrgtData.Options & TOpenOption.ofAllowMultiSelect) == TOpenOption.ofAllowMultiSelect;
    }
    this.state = {
      data: this.control.init(this) as UpdateOpenDialogContent,
      vcxVersion: -1,
      title: this.allowMultipleSelect ? "Výběr souborů" : "Výběr souboru",
    };
    this.control.RepeatBtn.setExecuteFce(this.handleRepeat);
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateOpenDialogContent>) => {
      return { data: state as UpdateOpenDialogContent };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentDidUpdate(prevProps: Readonly<WithContextPlacementProps>, prevState: Readonly<OpenFileDialogState>, snapshot?: any): void {
    if (prevState != this.state) {
      if (!prevState.data.Visible && prevState.data.Visible !== this.state.data.Visible) {
        if (this.input) {
          this.input.click();
        }
      }

      if (prevState.filesInMemoryCount !== this.state.filesInMemoryCount && this.state.filesInMemoryCount > 0) {
        this.control.RepeatBtn.updateState({ Enabled: false } as CSUpdateCommandItem);
        this.control.CloseBtn.updateState({ Enabled: false } as CSUpdateCommandItem);
        this.setState({ title: this.allowMultipleSelect ? "Načítání souborů" : "Načítání souboru" });
      }

      if (!prevState.readyToUpload && this.state.readyToUpload === true && this.state.filesInMemoryCount > 0) {
        this.control.beginUpload(this.state.filesInMemoryCount);
      }

      if (
        this.state.data.ExpectedFilesCount > 0 &&
        this.files.length >= this.state.data.ExpectedFilesCount &&
        (!prevState.data.ExpectedFilesCount || this.state.data.ExpectedFilesCount < prevState.data.ExpectedFilesCount)
      ) {
        this.setState({
          title: this.allowMultipleSelect
            ? `Nahrávání souborů na server (${this.files.length - this.state.data.ExpectedFilesCount + 1}/${this.files.length})`
            : "Nahrávání souboru na server",
        });
        this.control.uploadfile(this.files[this.files.length - this.state.data.ExpectedFilesCount]);
        this.control.RepeatBtn.updateState({ GlyphId: "wui*OK", Enabled: true } as CSUpdateCommandItem);
        this.control.CloseBtn.updateState({ Enabled: true } as CSUpdateCommandItem);
      }
    }

    if (prevState.data.ExpectedFilesCount > 0 && this.state.data.ExpectedFilesCount === 0) {
      this.control.endUpload();
    }
  }

  close(): Promise<void> {
    return Promise.resolve();
  }

  show(): Promise<void> {
    return Promise.resolve();
  }

  private handleRepeat = (ctrl: ClientNclButton) => {
    if (!this.state.filesInMemoryCount) if (this.input) this.input.click();
  };

  render(): JSX.Element {
    let files: JSX.Element[];
    if (this.input && this.input.files && this.input.files.length > 0) {
      files = [];
      for (let index = 0; index < this.input.files.length; index++) {
        const file = this.input.files[index];
        if (file) {
          files.push(<_FileItem key={file.name} file={file} addFile={this.addFile} vcx={this.control.VCX} />);
        }
      }
    }

    return (
      <>
        <div className={this.props.renderer.renderRule(fileDialogView, { vcx: this.control.VCX })}>
          <input
            style={{ display: "none" }}
            type="file"
            ref={(ref) => {
              this.input = ref;
            }}
            multiple={this.allowMultipleSelect}
            onClick={this.handleInputClick}
            onChange={this.handleChange}
          />
          <div className={this.props.renderer.renderRule(dialogView, { vcx: this.control.VCX })}>
            <div className={this.props.renderer.renderRule(dialogMessage, { vcx: this.control.VCX })}>
              <p>{this.state.title}</p>
            </div>
            {!this.state.filesInMemoryCount && (
              <K2Img
                glyphId="wui*mt.location.from"
                height={96}
                width={96}
                vcx={this.control.VCX}
                className={this.props.renderer.renderRule(dialogImg, { vcx: this.control.VCX })}
              />
            )}
            {this.state.filesInMemoryCount > 0 && <div className={this.props.renderer.renderRule(dialogFiles, { vcx: this.control.VCX })}>{files}</div>}
            <div className={this.props.renderer.renderRule(dialogBtns, { vcx: this.control.VCX })}>
              <K2Button controlUID={this.control.RepeatBtn.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} fillSvg={true} />
              <K2Button controlUID={this.control.CloseBtn.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} fillSvg={true} />
            </div>
          </div>
        </div>
      </>
    );
  }

  private handleInputClick = (e: React.MouseEvent<HTMLDivElement>) => {
    let __this = this;
    window.onfocus = (e: any) => {
      window.onfocus = undefined;
      __this.timeH = setTimeout(() => {
        // __this.control.appendFunction({ Name: cJSonFunctionCloseView, Args: null }, true);
      }, 7000);
    };
  };

  private handleChange = (e: React.ChangeEvent<HTMLDivElement>) => {
    if (this.timeH) {
      clearTimeout(this.timeH);
    }
    if (!this.input.files || this.input.files.length <= 0) {
      this.control.appendFunction({ Name: cJSonFunctionCloseView, Args: null }, true);
    }

    this.setState({ filesInMemoryCount: this.allowMultipleSelect ? this.input.files.length : 1 });
  };

  private addFile = (item: CSFile) => {
    this.files.push(item);

    if (this.files.length === this.input.files.length) {
      this.setState({ readyToUpload: true });
    }
  };
}

export const K2OpenDialogContent = withContext(_OpenDialogContent);

interface FileItemProps {
  file: File;
  addFile: (item: CSFile) => void;
  vcx: VisualContext;
}

interface FileItemState {
  loadedFile?: CSFile;
}

class _FileItem extends React.PureComponent<FileItemProps, FileItemState> {
  private reader: FileReader;

  constructor(props: FileItemProps) {
    super(props);
    this.state = {};
  }

  componentDidMount(): void {
    this.reader = new FileReader();
    this.reader.onloadend = (ev) => {
      let data = ev.target.result;
      if (data instanceof ArrayBuffer) {
        data = btoa(
          new Uint8Array(data).reduce(function (data, byte) {
            return data + String.fromCharCode(byte);
          }, "")
        );
      }
      this.setState({
        loadedFile: {
          FileName: this.props.file.name,
          Data: data,
          __type: cClientFileType,
        },
      });
    };

    this.reader.onabort = (ev) => {
      this.props.addFile(undefined);
    };

    this.reader.readAsArrayBuffer(this.props.file);
  }

  componentDidUpdate(prevProps: Readonly<FileItemProps>, prevState: Readonly<FileItemState>, snapshot?: any): void {
    if (this.state != prevState && this.state.loadedFile) {
      this.props.addFile(this.state.loadedFile);
    }
  }

  private formatBytes(bytes: number, decimals: number) {
    if (bytes == 0) return "0 Bytes";
    var k = 1024,
      dm = decimals || 2,
      sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  render(): JSX.Element {
    return (
      <div style={{ flex: "0 0 auto", padding: "4PX" }}>
        <div style={{ width: "16px", marginRight: "8px" }}>
          {this.state.loadedFile && <K2Img glyphId="wui*commitchange" height={16} width={16} vcx={this.props.vcx} style={{ flex: "0 0 auto" }} />}
        </div>
        <K2TruncateText style={{ flex: "2 1 auto" }}>{this.props.file.name}</K2TruncateText>
        <div style={{ flex: "0 0 auto" }}>{`${this.formatBytes(this.props.file.size, 1)}`}</div>
      </div>
    );
  }
}
